/**
 * Current Prices Tab - Searchable, filterable list of all active prices
 *
 * Features:
 * - Search by SKU or product name
 * - Filter by category, vendor
 * - Sort by any column
 * - Export to CSV (TODO)
 * - View price history for individual product (TODO)
 */

import React, { useState } from 'react'
import { Space, Card, Input, Select, Button, Table, Tag, Typography } from 'antd'
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons'
import type { TableColumnsType } from 'antd'
import { useCurrentPrices } from '../hooks/usePriceData'
import type { Price, PriceFilters } from '../types'

const { Text } = Typography

export const CurrentPricesTab: React.FC = () => {
  const [filters, setFilters] = useState<PriceFilters>({})

  const { data, isLoading } = useCurrentPrices(filters)
  const prices = data?.data || []
  const total = data?.total || 0

  const handleExportCSV = () => {
    // TODO: Implement CSV export
    console.log('Export CSV clicked')
  }

  const columns: TableColumnsType<Price> = [
    {
      title: 'SKU',
      dataIndex: 'productId',
      key: 'productId',
      width: 120,
      sorter: (a, b) => a.productId.localeCompare(b.productId),
      fixed: 'left',
    },
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: 'Price',
      dataIndex: 'currentPrice',
      key: 'currentPrice',
      width: 100,
      render: (price: number) => <Text strong>${price.toFixed(2)}</Text>,
      sorter: (a, b) => a.currentPrice - b.currentPrice,
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      width: 100,
      render: (cost: number) => <Text type="secondary">${cost.toFixed(2)}</Text>,
      sorter: (a, b) => a.cost - b.cost,
    },
    {
      title: 'Margin',
      dataIndex: 'margin',
      key: 'margin',
      width: 100,
      render: (margin: number) => (
        <Tag color={margin < 30 ? 'red' : margin < 40 ? 'orange' : 'green'}>
          {margin.toFixed(1)}%
        </Tag>
      ),
      sorter: (a, b) => a.margin - b.margin,
    },
    {
      title: 'Last Updated',
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime(),
    },
    {
      title: 'Rule Applied',
      dataIndex: 'ruleName',
      key: 'ruleName',
      width: 180,
      ellipsis: true,
    },
  ]

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Filters */}
      <Card size="small">
        <Space wrap>
          <Input.Search
            placeholder="Search by SKU or name..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
            allowClear
          />
          <Select
            placeholder="All Categories"
            value={filters.category}
            onChange={(v) => setFilters({ ...filters, category: v })}
            style={{ width: 200 }}
            allowClear
          >
            {/* TODO: Load from categories API */}
            <Select.Option value="hardware">Hardware</Select.Option>
            <Select.Option value="tools">Tools</Select.Option>
            <Select.Option value="supplies">Supplies</Select.Option>
          </Select>
          <Select
            placeholder="All Vendors"
            value={filters.vendorId}
            onChange={(v) => setFilters({ ...filters, vendorId: v })}
            style={{ width: 200 }}
            allowClear
          >
            {/* TODO: Load from vendors API */}
            <Select.Option value="vendor-1">ABC Corp</Select.Option>
            <Select.Option value="vendor-2">XYZ Inc</Select.Option>
          </Select>
          <Button icon={<DownloadOutlined />} onClick={handleExportCSV}>
            Export CSV
          </Button>
        </Space>
      </Card>

      {/* Summary */}
      <Card size="small">
        <Space>
          <Text>
            Showing <Text strong>{prices.length}</Text> of <Text strong>{total}</Text> prices
          </Text>
        </Space>
      </Card>

      {/* Price Table */}
      <Table
        columns={columns}
        dataSource={prices.map((p) => ({ ...p, key: p.uid }))}
        loading={isLoading}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showTotal: (total) => `${total} prices`,
          pageSizeOptions: ['20', '50', '100', '200'],
        }}
        scroll={{ x: 'max-content' }}
      />
    </Space>
  )
}
